import { useEffect, useRef, useState } from "react"
import FlexRow from "../../../components/base/FlexRow"
import Icon from "../../../components/base/Icon"
import "./ChatMessageItem.css"
import { CompactJob, CompactUser } from "../../../core/CompactModels"
import ChatMessage, { ChatMediaItem } from "../models/ChatMessage"
import { callRecordingUrl, canListActiveJobs, canManageJobs, getOwner, getUser, goToMessage, isDemo, openJobQuickView, openMessageToJob, toPrettyDate, useEnvironment, useHasCallRecording, useHub, useJobs, useScreen, useSocket } from "../../../core/Global"
import { API_ROUTES } from "../../../core/Constants"
import { CallTypes, MediaTypes, UserTypes } from "../../../core/Enums"
import DropdownMenu from "../../../components/menu/DropdownMenu"
import { copyToClipboard } from "../../../core/Helper"
import { toastError } from "../../../components/base/Toast"
import ChatContactItem from "../models/ChatContactItem"
import Avatar from "../../../components/base/Avatar"
import Modal from "../../../components/base/Modal"
import { ImagePreviewModal } from "../../../components/modals/ImagePreviewModal"
import { ChatAudioPlayer } from "../../../components/chat/ChatAudioPlayer"
import { isNativeApp } from "../../../NativeAppBrdige"


interface IProps {
    chat: ChatContactItem
    message: ChatMessage
    lastSender?: CompactUser
    preview?: boolean
    isGroup?: boolean
    onAssignToJob?: (msg: ChatMessage) => void
    onClaiming?: (msg: ChatMessage) => void
    onClaimed?: (msg: ChatMessage) => void
    onReply?: (msg: ChatMessage) => void
}

export default function ChatMessageItem(props: IProps) {

    const hub = useHub()
    const env = useEnvironment()
    const owner = getOwner()
    const user = getUser()
    const screen = useScreen()
    const hasCallRecording = useHasCallRecording()
    const activeJobs = useJobs()


    const [isRecipient, isRecipientSet] = useState<boolean>(false)
    const [isImage, isImageSet] = useState<boolean>(false)
    const [isAudio, isAudioSet] = useState<boolean>(false)
    const [isVideo, isVideoSet] = useState<boolean>(false)
    const [isFile, isFileSet] = useState<boolean>(false)

    const [claiming, claimingSet] = useState<boolean>(false)

    const [failed, failedSet] = useState<boolean>(false)

    const [imagePreview, imagePreviewSet] = useState<ChatMediaItem>()

    const [swipeDx, swipeDxSet] = useState<number>(0)
    const [swiping, swipingSet] = useState<boolean>(false)
    const swipeStartX = useRef<number | null>(null)
    const swipeStartY = useRef<number | null>(null)
    const swipeLocked = useRef<boolean>(false)

    useEffect(() => {
        if (props.message.id) {

            isImageSet(false)
            isAudioSet(false)
            isVideoSet(false)
            isFileSet(false)

            if (props.message.media) {
                if (props.message.media.mediaType == MediaTypes.Image) isImageSet(true);
                else if (props.message.media.mediaType == MediaTypes.Audio) isAudioSet(true);
                else if (props.message.media.mediaType == MediaTypes.Video) isVideoSet(true);
                else isFileSet(true);
            }
        }
    }, [props.message])

    useEffect(() => {
        if (props.message) {
            if (props.chat.isSystem) {
                // Internal team chat: each viewer's own messages go right, everyone else's go left with sender style.
                const mine = props.message.isMe
                    || (!!user?.id && (props.message.senderUserId === user.id || props.message.senderUser?.id === user.id))
                isRecipientSet(!mine)
            }
            else if (props.message.isMe) isRecipientSet(false)
            else if (user?.userType == UserTypes.Technician && props.chat.tech) isRecipientSet(!(props.message.isRecipient ?? false))
            else isRecipientSet(props.message.isRecipient ?? false)
        }

    }, [user, props.message])

    // Every hook is above this line, and these have to stay below it. This project's ESLint
    // reads a plain const sitting between two hooks as a branch, and reports every hook after
    // it as "called conditionally" -- seven errors describing nothing real, and a build that
    // will not compile. Nothing here is a hook; it is all derived from props.
    const native = isNativeApp()
    const isBot = props.message?.senderUser?.username === "rinvox"
    // Not in a read-only chat: there is nobody to reply to. This covers the swipe gesture on
    // the phone as well as the menu item.
    const canReply = !props.chat?.readOnly && !!props.onReply && !props.preview && !!props.message.id && !props.message.uploading && !props.message.sending
    const SWIPE_TRIGGER = -60
    const SWIPE_CLAMP = -100

    const onTouchStart = (e: React.TouchEvent) => {
        if (!native || !canReply) return
        swipeStartX.current = e.touches[0].clientX
        swipeStartY.current = e.touches[0].clientY
        swipeLocked.current = false
        swipingSet(true)
    }
    const onTouchMove = (e: React.TouchEvent) => {
        if (!native || swipeStartX.current == null) return
        const dx = e.touches[0].clientX - swipeStartX.current
        const dy = e.touches[0].clientY - (swipeStartY.current ?? 0)
        if (!swipeLocked.current) {
            if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
                swipeStartX.current = null
                swipingSet(false)
                swipeDxSet(0)
                return
            }
            if (Math.abs(dx) > 8) swipeLocked.current = true
        }
        if (dx > 0) { swipeDxSet(0); return }
        swipeDxSet(Math.max(dx, SWIPE_CLAMP))
    }
    const onTouchEnd = () => {
        if (!native || swipeStartX.current == null) return
        const final = swipeDx
        swipeStartX.current = null
        swipingSet(false)
        if (final <= SWIPE_TRIGGER && props.onReply) {
            props.onReply(props.message)
        }
        swipeDxSet(0)
    }



    const toHiddenDemoText = (txt: string) => {
        const noBrackets = txt.replace(/\[.*?\]/g, '');
        const noPhoneDetails = noBrackets.replace(/(Phone:\s*)\([^\)]+\)/g, '$1XXXXXXXX');
        const noAddressDetails = noPhoneDetails.replace(/(Address:\s*)[^\n]+/g, '$1XXXXXXXXXXXXXXX');

        return noAddressDetails;
    }


    // Same rules as a #JOBID written in the text: a technician only opens their own active jobs,
    // and nobody without ListActiveJobs opens one at all.
    const canOpenJob = (jobId?: string) => {
        if (!jobId || !canListActiveJobs()) return false
        if (user?.userType == UserTypes.Technician && !(activeJobs ?? []).some(j => j.jobId === jobId)) return false
        return true
    }

    const parseMessageText = (text?: string) => {
        if (!text) return null;

        const LINK_REGEX = /(https?:\/\/[^\s]+)/g;
        const JOB_ID_REGEX = /#([A-Z0-9]{7})/g;

        const parts = text.split(/(https?:\/\/[^\s]+|#[A-Z0-9]{7})/g).map((part, index) => {
            if (part.match(LINK_REGEX)) {
                return (
                    <a key={index} href={part} target="_blank" rel="noopener noreferrer">
                        {part}
                    </a>
                );
            } else if (part.match(JOB_ID_REGEX)) {

                const jobId = part.substring(1)

                // For a technician, only link the job if it's one of their active jobs; if the job
                // isn't in their active state (e.g. reassigned away), render it as plain text.
                if (user?.userType == UserTypes.Technician && !(activeJobs ?? []).some(j => j.jobId === jobId)) {
                    return part;
                }

                // Without ListActiveJobs the job can't be opened, so it isn't a link either.
                if (!canListActiveJobs()) return part;

                return (
                    <a href="#" key={index}
                        onClick={e => {
                            e.preventDefault()
                            openJobQuickView(jobId)
                        }}>
                        {part}
                    </a>
                );

            }

            return part;
        });

        return parts;
    }

    // Technicians never see call logs in their own app.
    const hideForTech = user?.userType == UserTypes.Technician && !!props.message?.callLog

    // Call-log display values (when this message is a call record).
    const callLog = props.message?.callLog
    const callOutgoing = callLog?.callType == CallTypes.Outgoing
    const callMissed = callLog ? !callLog.answered : false
    const callIcon = callMissed ? "phone-xmark" : callOutgoing ? "phone-arrow-up-right" : "phone-arrow-down-left"
    const callTitle = callMissed ? (callOutgoing ? "No answer" : "Missed Call") : (callOutgoing ? "Outgoing call" : "Incoming call")
    const callDur = callLog?.duration ?? 0
    const callDurText = `${Math.floor(callDur / 60)}:${(callDur % 60).toString().padStart(2, "0")}`
    const recordingUrl = callLog ? callRecordingUrl(owner?.id, callLog.callId) : ""

    return (
        <>
            {
                (failed || hideForTech) ? <></>
                    : props.message.emptyRow ?
                        <div style={{ height: 60 }}></div>
                        :
                        <div id={"chatmsg-" + props.message.id} className="chat-message-wrap position-relative"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                            onTouchCancel={onTouchEnd}>
                            {
                                native && canReply && swipeDx < -5 &&
                                <div className="chat-message-swipe-indicator"
                                    style={{ opacity: Math.min(1, Math.abs(swipeDx) / Math.abs(SWIPE_TRIGGER)) }}>
                                    <Icon icon="reply" type="solid" size={20} />
                                </div>
                            }
                            <div className={"d-flex mb-2" + (isRecipient ? " justify-content-start" : " flex-row-reverse")}
                                style={{
                                    transform: native ? `translateX(${swipeDx}px)` : undefined,
                                    transition: native && !swiping ? "transform 0.2s ease" : "none"
                                }}>

                                {
                                    user?.userType == UserTypes.Technician ? <></>
                                        : (!isRecipient || props.isGroup) ?
                                            <>
                                                {
                                                    props.lastSender?.id == props.message.senderUser?.id ?
                                                        <div className="symbol symbol-35px symbol-circle">
                                                            <div style={{ width: 35 }}></div>
                                                        </div>
                                                        : isBot ?
                                                            <div className="symbol symbol-35px symbol-circle" title="Rinvox Bot">
                                                                <img alt="Rinvox Bot" src="/assets/brands/rinvox/icon.png" />
                                                            </div>
                                                        : props.message.senderUser?.avatarUrl ?
                                                            <div className="symbol symbol-35px symbol-circle" title={props.message.senderUser?.fullName}>
                                                                <img alt={props.message.senderUser?.fullName} src={props.message.senderUser?.avatarUrl} />
                                                            </div>
                                                            :
                                                            <div className="symbol symbol-35px symbol-circle" title={props.message.senderUser?.fullName}>
                                                                <span className="symbol-label bg-light-danger text-danger fs-1 fw-bolder">{props.message.senderUser?.fullName?.substring(0, 1).toUpperCase()}</span>
                                                            </div>
                                                }
                                            </>
                                            : <></>
                                }

                                <div className={"d-flex flex-column flex-grow-1 mx-3 " + (isRecipient ? "align-items-start" : "align-items-end")}
                                    style={{ minWidth: 0 }}>

                                    {
                                        user?.userType == UserTypes.Technician ? <></>
                                            : ((!isRecipient || props.isGroup) && props.lastSender?.id != props.message.senderUser?.id) ?
                                                <div className="d-flex align-items-center justify-content-end mb-1">

                                                    <div className="ms-3">
                                                        <a className="fs-8 text-gray-900 me-1">
                                                            {
                                                                isDemo() ? "User" : props.message.senderUser?.firstName
                                                            }
                                                        </a>
                                                        {/* <span className="text-muted fs-7 mb-1">2 mins</span> */}
                                                    </div>

                                                </div>
                                                : <></>
                                    }

                                    <div className={"chat-bubble-row d-flex align-items-center" + (isRecipient ? "" : " flex-row-reverse")}>

                                        <div className={"p-3 rounded chat-message-text " + ((isRecipient) ? "bg-light-info" : "bg-chat-system")}
                                            style={{
                                                whiteSpace: "pre-line",
                                                width: (isImage || isVideo) ? "auto" : screen.isMobile ? "auto" : 500,
                                                maxWidth: (isImage || isVideo) ? "auto" : screen.isMobile ? "85%" : "75%",
                                                minWidth: 0,
                                                overflow: "hidden",
                                                wordBreak: "break-word"
                                            }}>

                                            {
                                                props.message.replyToMessage &&
                                                <div className="chat-reply-preview"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        if (props.message.replyToMessage?.id) goToMessage(props.message.replyToMessage.id);
                                                    }}>
                                                    <div className="chat-reply-preview-sender">
                                                        {isDemo() ? "User" : (props.message.replyToMessage.senderName ?? "Unknown")}
                                                    </div>
                                                    <div className="chat-reply-preview-text">
                                                        {
                                                            props.message.replyToMessage.previewIcon &&
                                                            <Icon icon={props.message.replyToMessage.previewIcon} className="me-1" size={10} />
                                                        }
                                                        {props.message.replyToMessage.text}
                                                    </div>
                                                </div>
                                            }

                                            {
                                                props.message.callLog ?
                                                    <div>
                                                        <FlexRow>
                                                            <div className="symbol symbol-35px symbol-circle me-2">
                                                                <div className={"symbol-label " + (callMissed ? "bg-light-danger" : "bg-light-success")}>
                                                                    <Icon icon={callIcon} size={16} className={callMissed ? "text-danger" : "text-success"} />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="fw-bold">{callTitle}</div>
                                                                {!callMissed && callDur > 0 && <div className="fs-8 text-muted">{callDurText}</div>}
                                                            </div>
                                                        </FlexRow>
                                                        {
                                                            hasCallRecording && !callMissed && callLog.hasRecording &&
                                                            <div className="mt-2">
                                                                <ChatAudioPlayer src={recordingUrl}
                                                                    onPlay={() => {
                                                                        // Same as playing it from the calls list: the call's
                                                                        // Listeners column shows who listened. The server
                                                                        // records each user once.
                                                                        if (callLog.callId) hub.Post(API_ROUTES.Calls + "listenstart", { id: callLog.callId }, () => { });
                                                                    }} />
                                                            </div>
                                                        }
                                                    </div>
                                                : isFile ?
                                                    <div>
                                                        <FlexRow>

                                                            {
                                                                props.message.uploading ?
                                                                    <>
                                                                        <div className="symbol symbol-35px symbol-circle">
                                                                            <div className="symbol-label bg-primary">
                                                                                <Icon icon="spinner-third" spin size={30} type="thin" className="text-light" />
                                                                            </div>
                                                                        </div>
                                                                        <div className="ms-2 limited-text mw-400px">
                                                                            {props.message.media?.fileName}
                                                                        </div>
                                                                    </>
                                                                    :
                                                                    <>
                                                                        <a href={`${env.hubUrl}/base/public/chat/file/download/${owner?.id}/${props.message.id}`} target="_blank" className="symbol symbol-35px symbol-circle" title="Click to Download">
                                                                            <div className="symbol-label bg-primary">
                                                                                <Icon icon="arrow-down" size={15} className="text-light" />
                                                                            </div>
                                                                        </a>
                                                                        <div className="ms-2" >
                                                                            <div>
                                                                                <a href={`${env.hubUrl}/base/public/chat/file/download/${owner?.id}/${props.message.id}`} className="text-gray-900" target="_blank" title="Click to Download">
                                                                                    {props.message.media?.fileName}
                                                                                </a>
                                                                            </div>
                                                                            <div className="fs-8 text-gray-600">
                                                                                {props.message.media?.fileSizeLabel}
                                                                            </div>
                                                                        </div>

                                                                    </>

                                                            }


                                                        </FlexRow>
                                                        {
                                                            !window.isNullOrEmpty(props.message.text) &&
                                                            <div className="mt-3">
                                                                {parseMessageText(props.message.text)}
                                                            </div>
                                                        }
                                                    </div>
                                                    : isImage ?
                                                        <div>

                                                            <FlexRow>
                                                                <Avatar avatarUrl={props.message.media?.fileUrl}
                                                                    width={250} type="square" background="dark"
                                                                    onClick={() => imagePreviewSet(props.message.media)} />
                                                            </FlexRow>

                                                            {
                                                                !window.isNullOrEmpty(props.message.text) &&
                                                                <div className="mt-3">
                                                                    {parseMessageText(props.message.text)}
                                                                </div>
                                                            }
                                                        </div>
                                                        : isVideo ?
                                                            <div>
                                                                {/* A still of the clip, the size of a photo, with a play button over it;
                                                                    it plays in the large viewer, as a photo opens there. */}
                                                                <div className="chat-video-thumb"
                                                                    onClick={() => imagePreviewSet(props.message.media)}>
                                                                    <video src={(props.message.media?.fileUrl ?? "") + "#t=0.1"}
                                                                        preload="metadata"
                                                                        muted
                                                                        playsInline />
                                                                    <span className="chat-video-play">
                                                                        <Icon icon="play" type="solid" size={20} />
                                                                    </span>
                                                                </div>

                                                                {
                                                                    !window.isNullOrEmpty(props.message.text) &&
                                                                    <div className="mt-3">
                                                                        {parseMessageText(props.message.text)}
                                                                    </div>
                                                                }
                                                            </div>
                                                            : isAudio ?
                                                                <div>
                                                                    <ChatAudioPlayer src={props.message.media?.fileUrl ?? ""} />
                                                                    {/* <audio src={props.message.media?.fileUrl} className="w-100" controls></audio>

                                                            {
                                                                !window.isNullOrEmpty(props.message.text) &&
                                                                <div className="mt-3">
                                                                    {parseMessageText(props.message.text)}
                                                                </div>
                                                            } */}
                                                                </div>
                                                                :
                                                                <div className="fs-6">
                                                                    {
                                                                        isDemo() ? toHiddenDemoText(props.message.text ?? "")
                                                                            : parseMessageText(props.message.text)
                                                                    }

                                                                    {
                                                                        props.message.isJob && (isRecipient || env.isDevelopment) && user?.userType != UserTypes.Technician && canManageJobs() &&
                                                                        <div className="mb-2 text-end">
                                                                            <a href="#" target="_blank" className={"btn btn-info btn-xs fs-8"}
                                                                                onClick={e => {
                                                                                    e.preventDefault();
                                                                                    if (props.message.text && props.message.id) openMessageToJob(props.message.text, props.message.id)
                                                                                }}>
                                                                                Add as Job
                                                                            </a>
                                                                        </div>
                                                                    }

                                                                </div>
                                            }
                                            {
                                                /* Why an undelivered message did not arrive, in the carrier's own terms
                                                   -- "Not delivered (30006): the number is a landline ...". Above the
                                                   footer rather than instead of it, so the time and the red icon stay. */
                                                props.message.messagesStatus === "undelivered" && !window.isNullOrEmpty(props.message.failedError) &&
                                                <div className="text-danger fs-8 mt-2 text-end">
                                                    {props.message.failedError}
                                                </div>
                                            }
                                            <FlexRow className="chat-message-footer">

                                                {
                                                    (props.message.messagesStatus == "failed" && !window.isNullOrEmpty(props.message.failedError)) ?
                                                        <div className="text-danger fs-8 mt-2 text-end">
                                                            {props.message.failedError}
                                                        </div>
                                                        :
                                                        <>
                                                            {
                                                                props.message.uploading ?
                                                                    <div className="fs-9">
                                                                        Uploading...
                                                                    </div>
                                                                    :
                                                                    props.message.sending ?
                                                                        <div className="fs-9">
                                                                            Sending...
                                                                        </div>
                                                                        :
                                                                        <FlexRow>
                                                                            {
                                                                                user?.userType == UserTypes.Technician ? <></>
                                                                                    :
                                                                                    <>
                                                                                        {
                                                                                            props.message.sentViaGSM &&
                                                                                            <Icon icon="signal-strong" doutone size={12} className="me-2" title="Sent via SMS" />
                                                                                        }
                                                                                        {
                                                                                            props.message.sentViaApp &&
                                                                                            <Icon icon="mobile-signal-out" doutone size={14} className="me-2" title="Sent via Rinvox App" />
                                                                                        }
                                                                                        {
                                                                                            props.message.sentViaTelegram &&
                                                                                            <Icon icon="telegram" type="brands" size={14} className="me-2" title="Sent via Telegram" />
                                                                                        }
                                                                                        {
                                                                                            props.message.sentViaWhatsApp &&
                                                                                            <Icon icon="whatsapp" type="brands" size={14} className="me-2" title="Sent via WhatsApp" />
                                                                                        }
                                                                                    </>
                                                                            }

                                                                            <div className="fs-9" style={{ height: 12 }}>
                                                                                {toPrettyDate(props.message.createdAt)}
                                                                            </div>
                                                                        </FlexRow>
                                                            }

                                                            {
                                                                props.message.job &&
                                                                (canOpenJob(props.message.job.jobId)
                                                                    ? <a href="#" className="fs-9 ms-2"
                                                                        title="Open job"
                                                                        onClick={e => {
                                                                            e.preventDefault()
                                                                            e.stopPropagation()
                                                                            openJobQuickView(props.message?.job?.jobId)
                                                                        }}>
                                                                        #{props.message.job.jobId}
                                                                    </a>
                                                                    : <div className="fs-9 ms-2">
                                                                        #{props.message.job.jobId}
                                                                    </div>
                                                                )
                                                            }

                                                            {
                                                                user?.userType == UserTypes.Technician ? <></>
                                                                    :
                                                                    <>
                                                                        {
                                                                            props.message.claimedUser &&
                                                                            <FlexRow className="ms-2">
                                                                                <Icon icon="check-circle" className="me-1 text-success" type="solid" />
                                                                                <div className="fs-8">
                                                                                    {isDemo() ? "User 1" : props.message.claimedUser.fullName}
                                                                                </div>
                                                                            </FlexRow>
                                                                        }

                                                                        {
                                                                            props.message.unread && !props.message.claimedUser && (user?.isCustomer || user?.isTech || user?.isSystemAdmin || props.chat.accessAll || props.chat.accessClaimUserIds?.includes(user?.id ?? "0000")) &&
                                                                            <FlexRow className="ms-2"
                                                                                onClick={e => {
                                                                                    if (props.onClaiming) props.onClaiming(props.message);
                                                                                    claimingSet(true);

                                                                                    setTimeout(() => {
                                                                                        hub.Post(API_ROUTES.ChatClaim, {
                                                                                            id: props.message.id
                                                                                        }, res => {
                                                                                            claimingSet(false)
                                                                                            if (props.onClaimed) props.onClaimed(props.message)
                                                                                        })
                                                                                    }, 500);

                                                                                }}>
                                                                                {
                                                                                    claiming ?
                                                                                        <>
                                                                                            <Icon icon="spinner-third" spin />
                                                                                            <div className="fs-8 fw-bold ms-2">
                                                                                                Please wait...
                                                                                            </div>
                                                                                        </>
                                                                                        :
                                                                                        <>
                                                                                            <Icon icon="check" className="me-1 text-danger" type="solid" />
                                                                                            <div className="fs-8 fw-bold text-danger">
                                                                                                Claim the message
                                                                                            </div>
                                                                                        </>
                                                                                }

                                                                            </FlexRow>
                                                                        }
                                                                    </>

                                                            }


                                                        </>
                                                }




                                                {
                                                    !props.preview &&
                                                    <FlexRow className="ms-auto">


                                                        {
                                                            !isRecipient &&
                                                            <div className="me-1">
                                                                {
                                                                    user?.userType == UserTypes.Technician ?
                                                                        <>
                                                                            {
                                                                                props.message.claimedUser ?
                                                                                    <Icon icon="check-double" className="text-primary" size={11} />
                                                                                    : <Icon icon="check-double" size={11} />
                                                                            }
                                                                        </>
                                                                        :
                                                                        <>
                                                                            {
                                                                                props.message.messagesStatus == "delivered" ?
                                                                                    <Icon icon="check-double" size={11} />
                                                                                    : props.message.messagesStatus == "seen" ?
                                                                                        <Icon icon="check-double" className="text-primary" size={11} />
                                                                                        : props.message.messagesStatus == "sent" ?
                                                                                            <Icon icon="check" size={11} />
                                                                                            : props.message.messagesStatus == "failed" ?
                                                                                                <Icon icon="circle-info" className="text-danger" size={11} tooltip="Failed to send" />
                                                                                                /* Accepted by the carrier's side but never reached the phone --
                                                                                                   Twilio's "undelivered". Red like a failure, drawn differently so
                                                                                                   the two can be told apart; it used to fall through to the clock,
                                                                                                   which read as still sending. */
                                                                                                : props.message.messagesStatus === "undelivered" ?
                                                                                                    <Icon icon="triangle-exclamation" className="text-danger" size={11}
                                                                                                        tooltip="Not delivered: the carrier could not deliver this message" />
                                                                                                    :
                                                                                                    <Icon icon="clock" size={11} />
                                                                            }
                                                                        </>
                                                                }

                                                            </div>
                                                        }

                                                        {
                                                            user?.userType == UserTypes.Technician ? <></>
                                                                :
                                                                <div>

                                                                    <a className={"cursor-pointer px-1 text-dark"} data-kt-menu-trigger="click" data-kt-menu-placement="bottom-end"
                                                                        onClick={e => {
                                                                            e.preventDefault();
                                                                            window.dataTableMenu();
                                                                        }}>
                                                                        <Icon icon="ellipsis-vertical" size={11} />
                                                                    </a>
                                                                    <DropdownMenu width={180}
                                                                        source={[
                                                                            {
                                                                                title: "Reply",
                                                                                icon: "reply",
                                                                                visible: canReply,
                                                                                action() {
                                                                                    if (props.onReply) props.onReply(props.message);
                                                                                },
                                                                            },
                                                                            {
                                                                                title: "Download",
                                                                                visible: (props.message.media ? true : false),
                                                                                icon: "download",
                                                                                redirect: `${env.hubUrl}/base/public/chat/file/download/${owner?.id}/${props.message.id}`
                                                                            },
                                                                            {
                                                                                title: "Copy Text",
                                                                                icon: "copy",
                                                                                async action() {
                                                                                    const cp = await copyToClipboard(props.message.text, "Message has been copied to the clipboard!");
                                                                                    if (!cp) toastError("Copy action is not supported in your browser!")
                                                                                },
                                                                            },
                                                                            {
                                                                                title: "Copy SID",
                                                                                icon: "copy",
                                                                                visible: ((props.message.sentViaGSM) && (props.message.messageSid != null) && (props.message.messageSid != "")),
                                                                                async action() {
                                                                                    const cp = await copyToClipboard(props.message.messageSid, "Message SID has been copied to the clipboard!");
                                                                                    if (!cp) toastError("Copy action is not supported in your browser!")
                                                                                },
                                                                            },
                                                                            {
                                                                                title: "Assign to Job",
                                                                                icon: "hashtag",

                                                                                // A notice from us is not a customer message and
                                                                                // does not belong on anybody's job.
                                                                                visible: !props.chat?.readOnly,
                                                                                action() {
                                                                                    if (props.message && props.onAssignToJob) props.onAssignToJob(props.message);
                                                                                },
                                                                            }
                                                                        ]} />

                                                                </div>
                                                        }




                                                    </FlexRow>
                                                }

                                            </FlexRow>





                                        </div>

                                        {
                                            !native && canReply &&
                                            <a href="#" className="chat-hover-reply-btn ms-2 me-2"
                                                title="Reply"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    if (props.onReply) props.onReply(props.message);
                                                }}>
                                                <Icon icon="reply" size={14} />
                                            </a>
                                        }

                                    </div>


                                </div>
                            </div >
                        </div>
            }


            {
                imagePreview?.fileUrl &&
                <ImagePreviewModal id={"chat-image-preview-" + props.message.id}
                    key={"chat-image-preview-" + props.message.id}
                    src={imagePreview.fileUrl}
                    video={imagePreview.mediaType == MediaTypes.Video}
                    onClose={() => imagePreviewSet(undefined)}
                />
            }

        </>
    )
}